import { aws } from './awsdb';
import { formatMoney } from './money';

// Curated activity log. Called from wherever a user-initiated mutation happens;
// rows render in InvoiceDrawer's Activity section. Fails silently (console) so a
// missing log row never blocks the actual mutation.
export async function logActivity(invoiceId, session, payload) {
  if (!invoiceId || !session) return;
  const row = {
    invoice_id: invoiceId,
    actor_code: session.code,
    actor_name: session.name,
    action_type: payload.action_type,
    before_value: payload.before_value ?? null,
    after_value: payload.after_value ?? null,
    notes: payload.notes ?? null
  };
  const { error } = await aws.from('invoice_activity_log').insert([row]);
  if (error) console.warn('[activity-log] insert failed:', error.message);
}

// Human-readable line for a log entry. Keep in sync with action_type values
// used at the call sites.
export function formatActivityLine(entry) {
  const a = entry.actor_name || 'Someone';
  const before = entry.before_value;
  const after = entry.after_value;
  const hasDetail = entry.notes?.startsWith('detail:');
  switch (entry.action_type) {
    case 'status_change': {
      if (hasDetail) {
        try {
          const d = JSON.parse(entry.notes.slice(7));
          if (d.type === 'approval') return `${a} approved ${after ? formatMoney(d.amount) : ''} for payment`;
        } catch {}
      }
      return `${a} changed status: ${humanStatus(before)} → ${humanStatus(after)}`;
    }
    case 'marked_disputed':
      return `${a} marked disputed${entry.notes && !hasDetail ? ` — ${entry.notes}` : ''}`;
    case 'payment_logged': {
      if (hasDetail) {
        try {
          const d = JSON.parse(entry.notes.slice(7));
          return `${a} logged payment: ${after || ''} via ${d.method || '—'}`;
        } catch {}
      }
      return `${a} logged payment: ${after || ''}`;
    }
    case 'payment_deleted':
      return `${a} removed payment: ${before || ''}`;
    case 'invoice_created':
      return `${a} created invoice`;
    case 'invoice_edited':
      return `${a} edited invoice${entry.notes ? ` (${entry.notes})` : ''}`;
    case 'line_items_updated':
      return `${a} updated line items: ${entry.after_value || ''}`;
    case 'attachment_added':
      return `${a} attached: ${after || '(file)'}`;
    case 'attachment_deleted':
      return `${a} removed attachment: ${before || '(file)'}`;
    case 'comment':
      return `${a} commented`;
    default:
      return `${a} · ${entry.action_type}`;
  }
}

export function formatActivityWhen(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  // Mercury numeric + short month: "16 Apr 2026, 11:42"
  return d.toLocaleString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false
  });
}

function humanStatus(code) {
  if (!code) return '—';
  return ({
    draft: 'Draft',
    approved: 'Approved',
    disputed: 'Disputed',
    paid: 'Paid'
  })[code] || code;
}
